import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useModal, usePage, useTheme } from "@/comn/hooks";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { comnUtils } from "@/comn/utils";
import { localeDatetimeSecond } from "@/comn/components/_";
import {
    APIS,
    SF_OGA_USR_INFO,
    SG_USR_AUTH_HIST_LIST,
    SG_USR_AUTH_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/usrauth/services/OsciOgaAuthService";
import { OSCI0104022Q } from "@/tz/go/egaz/osci/ptl/ptl/usrauth/OSCI0104022Q";
import { FIXED_USR_AUTH_IDS } from "@/tz/go/egaz/osci/ptl/usr/orgusr/services/OsciOrgUsrService";

/*
 * Save. OGA User Auth Data
 * !== 세관 사용자 권한 저장. ==!
 */
export const OSCI0104021S = (): JSX.Element => {
    const modal = useModal();
    const param = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { pgeUid } = usePage({
        auth: ["auth_osci0003", "auth_osci0002"],
    });

    const form = { ogaUsrInfo: useForm({ defaultSchema: SF_OGA_USR_INFO }) };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
        ogaUsrAuthHistList: useGrid({ defaultSchema: SG_USR_AUTH_HIST_LIST }),
    };

    const fetch = {
        getOgaUsrDtl: useFetch({
            api: () => APIS.getOgaUsrDtl({ tin: param["tin"] }),
            onSuccess: (data: any) => {
                const { content } = data.ogaUsrDtl;
                form.ogaUsrInfo.setValues(content);
                fetch.getOgaUsrAuthList.fetch();
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        getOgaUsrAuthList: useFetch({
            api: () => APIS.getOgaUsrAuthList({ tin: param["tin"] }),
            onSuccess: () => {
                fetch.getOgaUsrAuthHistList.fetch(param["tin"]);
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        getOgaUsrAuthHistList: useFetch({
            api: (data: any) => APIS.getOgaUsrAuthHistList(data),
            onSuccess: () => {},
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        saveOgaUsrAuthList: useFetch({
            api: () =>
                APIS.saveOgaUsrAuthList({
                    tin: param["tin"],
                    usrAuthList: grid.ogaUsrAuthList.getData({ excludes: ["origin"] }),
                }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00073",
                    close: false,
                    onConfirm: () => {
                        fetch.getOgaUsrDtl.fetch();
                    },
                });
            },
            onError: () => modal.openModal({ content: "msg.osc.00045" }), // Failed to save. !==  저장이 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_usrAuthList: {
            // 시스템이 관리하는 권한은 체크박스를 막아 삭제할 수 없도록 한다
            checkbox: (row: any) => !FIXED_USR_AUTH_IDS.includes(row.authId),
        },
        grid_usrAuthHistList: {
            cell: {
                // 그리드 기본 datetime 표시는 분까지만 나오므로 초까지 표시한다. (EN/TZ : DD/MM/YYYY HH:mm:ss)
                frstRgsrDtm: ({ value }: any) => localeDatetimeSecond(value, theme.lang),
            },
        },
    };

    const handler = {
        onBtnSaveClick: () => {
            const list = grid.ogaUsrAuthList.getData({ excludes: ["origin"] });
            if (list.length === 0) {
                modal.openModal({ content: "msg.osc.00218" }); // There is no data to be stored and deleted. !== 저장 및 삭제 대상 데이터가 없습니다. ==!
                return;
            }

            modal.openModal({
                content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                onConfirm: () => fetch.saveOgaUsrAuthList.fetch(),
            });
        },

        onBtnGrdAddRowClick: () => {
            const data = grid.ogaUsrAuthList.getData({ excludes: ["origin"] }); // INSERT, DELETE

            if (!comnUtils.isEmpty(data)) {
                modal.openModal({ content: "msg.osc.00219" }); // Data has been changed. Please save it and proceed. !== 데이터가 변경되었습니다. 저장 후 진행해 주세요. ==!
                return;
            }

            const modalCallback = (list: any) => {
                const authList = grid.ogaUsrAuthList.getData();
                list.forEach(
                    (item: any) =>
                        authList.findIndex((subItem: any) => item.authId === subItem.authId) === -1 &&
                        grid.ogaUsrAuthList.addRow(item),
                );
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "sm",
                content: (
                    <OSCI0104022Q
                        tin={param["tin"]}
                        ogaUsrAuthList={grid.ogaUsrAuthList.getData()}
                        callback={modalCallback}
                    />
                ),
            });
        },

        onBtnGrdDelRowClick: () => {
            if (comnUtils.isEmpty(grid.ogaUsrAuthList.getChecked())) {
                modal.openModal({ content: "msg.osc.00084" }); // No data has been selected.. !== 선택된 데이터가 없습니다. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.ogaUsrAuthList.deleteRow("checkbox");
                },
            });
        },
    };

    useEffect(() => {
        fetch.getOgaUsrDtl.fetch();
    }, [theme]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.ogaNm} />
                            <Group.Control {...form.ogaUsrInfo.schema.ogaDeptNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.tin} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.usrMoblNo} />
                            <Group.Control {...form.ogaUsrInfo.schema.usrEmlAddr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.ogaUsrInfo.schema.ogaTelno} />
                            <Group.Control {...form.ogaUsrInfo.schema.ogaFaxNo} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Layout.Right>
                            <Button role={"gridAdd"} onClick={handler.onBtnGrdAddRowClick} />
                            <Button role={"gridDelete"} onClick={handler.onBtnGrdDelRowClick} />
                        </Layout.Right>

                        <Grid
                            {...grid.ogaUsrAuthList.grid}
                            data={fetch.getOgaUsrAuthList.data?.ogaUsrAuthList}
                            render={render.grid_usrAuthList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_HIST_LIST"} />
                    <Group.Section gap={2}>
                        <Grid
                            {...grid.ogaUsrAuthHistList.grid}
                            data={fetch.getOgaUsrAuthHistList.data?.ogaUsrAuthHistList}
                            render={render.grid_usrAuthHistList}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Button role={"list"} onClick={() => navigate(`${URLS.osci0104020q}`)} />
                </Layout.Left>
                <Layout.Right>
                    <Button role={"save"} onClick={handler.onBtnSaveClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
